import React, { useEffect } from 'react';
import _get from 'lodash/get';

import { UiKit } from 'components';
import { useAccess, useFeatures, useRetailer } from 'utils/hooks';
import FeatureToggleCard from './FeatureToggleCard';
import FeatureToggleBadge from './FeatureToggleBadge';
import { FeatureFlagToggleContext } from './context';

/**
 * @param {Object} props
 * @param {string} props.featureType - Feature type
 * @param {import("dictionary").FEATURE_SLUGS} props.featureSlug - Feature slug
 * @param {boolean} props.defaultIsEnabled - Default enabled state
 * @param {Function} props.isActiveCallback - Callback function to get the active status
 */
export default function FeatureFlagToggle({
  featureType = '',
  featureSlug = '',
  defaultIsEnabled = false,
  isActiveCallback = () => {},
  children = null
}) {
  const {
    features,
    isLoading,
    isError,
    isModalOpen,
    isProcessing,
    modalData,
    onToggle,
    onModalClose,
    onModalClosed,
    toggleFeature
  } = useFeatures({ includeFeatures: [featureType], defaultIsEnabled });
  const { syncSubscription, hasSubFeatureAccess } = useAccess();
  const { isSSRStore } = useRetailer();

  const hasAccess = featureSlug ? hasSubFeatureAccess(featureSlug) : true;
  const feature = _get(features, '[0]', null);
  const id = _get(feature, 'id', null);
  const title = _get(feature, 'title', '');
  const docUrl = _get(feature, 'documentation_url', '');

  const isEnabled = hasAccess && _get(feature, 'is_enabled', false);
  const isEnabling = !_get(modalData, 'is_enabled', false);

  useEffect(() => {
    syncSubscription();
  }, [syncSubscription]);

  useEffect(() => {
    isActiveCallback(isEnabled);
  }, [isEnabled, isActiveCallback]);

  if (!featureType || isError) return null;

  return (
    <FeatureFlagToggleContext.Provider
      value={{
        id,
        isLoading,
        isEnabled,
        isProcessing,
        docUrl: isSSRStore ? '' : docUrl,
        onModalOpen: onToggle
      }}
    >
      {children}
      <UiKit.Modal
        isOpen={isModalOpen}
        isProcessing={isProcessing}
        onClose={onModalClose}
        onClosed={onModalClosed}
      >
        <UiKit.Modal.Header title={title} />
        <UiKit.Modal.Body>
          <p>
            Are you sure you want to {isEnabling ? 'enable' : 'disable'} this
            feature?
          </p>
        </UiKit.Modal.Body>
        <UiKit.Modal.Footer
          onConfirm={toggleFeature}
          confirmBtnVariant={isEnabling ? 'primary' : 'destructive'}
          confirmBtnText={isEnabling ? 'Enable' : 'Disable'}
        />
      </UiKit.Modal>
    </FeatureFlagToggleContext.Provider>
  );
}

FeatureFlagToggle.CardTrigger = FeatureToggleCard;
FeatureFlagToggle.BadgeTrigger = FeatureToggleBadge;
